import NeptuneElement from '../../NeptuneElement.js';

/**
 * @class Meter
 * @extends NeptuneElement
 *
 * Wraps the native <meter> element to display a value's position within a bounded
 * range, automatically coloring the fill and label based on the value's gauge region.
 */
class Meter extends NeptuneElement {

    static METER_STATES = ['low', 'medium', 'high'];

    templateMarkup = `
        <div>
            <label></label>
            <meter></meter>
            <span></span>
        </div>
    `;
    componentCssUrl = '/src/components/core/meter/meter.css';

    /**
     * Creates a new Meter instance.
     */
    constructor() {
        super();
    }

    /**
     * Renders the component, links accessibility attributes, resolves the initial
     * value/min/max/low/high/optimum range, and processes the initial label/supporting-text.
     *
     * @returns {Promise<void>}
     */
    async connectedCallback() {
        await super.connectedCallback();
        this.handleAccessibilityAttributes();

        const meterElement = this.shadowRoot.querySelector('meter');
        if (meterElement) {
            this.updateMeterRange(meterElement);
        }
        this.processInitialAttributesAfterRender(['label', 'supporting-text']);
    }

    /**
     * Attribute names observed by the component for reactive updates.
     *
     * @returns {string[]} The list of observed attribute names.
     */
    static get observedAttributes() {
        return ['value', 'min', 'max', 'low', 'high', 'optimum', 'label', 'supporting-text'];
    }

    /**
     * Called when an observed attribute changes. Delegates to {@link processAttribute}
     * once the component has completed its initial render.
     *
     * @param {string} name - The attribute name.
     * @param {string|null} oldValue - The previous attribute value.
     * @param {string|null} newValue - The new attribute value.
     * @returns {void}
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    /**
     * Evaluates a single attribute change and updates the shadow `<meter>` element,
     * label, or supporting text accordingly.
     *
     * @param {string} name - The attribute name.
     * @param {string|null} oldValue - The previous attribute value.
     * @param {string|null} newValue - The new attribute value.
     * @returns {void}
     */
    processAttribute(name, oldValue, newValue) {
        const meterElement = this.shadowRoot.querySelector('meter');
        if (!meterElement) {
            return;
        }

        switch (name) {
            case 'value':
            case 'min':
            case 'max':
            case 'low':
            case 'high':
            case 'optimum': {
                this.updateMeterRange(meterElement);
                break;
            }
            case 'label': {
                this.setLabel(newValue);
                break;
            }
            case 'supporting-text': {
                this.setSupportingText(newValue);
                break;
            }
            default: {
                break;
            }
        }
    }

    /**
     * Resolves value/min/max/low/high/optimum using the native <meter> fallback and clamping
     * rules, reflects the resolved values onto the shadow element, and derives the gauge
     * region so CSS can color the fill and label without relying on browser-specific pseudo-elements.
     *
     * @param {HTMLMeterElement} meterElement - The native <meter> element in the shadow DOM.
     * @returns {void}
     */
    updateMeterRange(meterElement) {
        const min = this.resolveMin();
        const max = this.resolveMax(min);
        const value = this.resolveValue(min, max);
        const low = this.resolveLow(min, max);
        const high = this.resolveHigh(min, max, low);
        const optimum = this.resolveOptimum(min, max);

        meterElement.setAttribute('min', min);
        meterElement.setAttribute('max', max);
        meterElement.setAttribute('value', value);
        meterElement.setAttribute('low', low);
        meterElement.setAttribute('high', high);
        meterElement.setAttribute('optimum', optimum);

        const state = this.resolveGaugeRegion({ value, min, max, low, high, optimum });
        meterElement.setAttribute('data-state', state);
        this.setAttribute('data-state', state);
    }

    /**
     * Resolves the effective `min` value, defaulting to `0` when missing or malformed.
     *
     * @returns {number} The resolved minimum.
     */
    resolveMin() {
        const min = parseFloat(this.getAttribute('min'));
        return isFinite(min) ? min : 0;
    }

    /**
     * Resolves the effective `max` value, defaulting to `1` when missing or malformed,
     * and forcing it to `min + 1` when it would otherwise not exceed `min`.
     *
     * @param {number} min - The resolved minimum value.
     * @returns {number} The resolved maximum.
     */
    resolveMax(min) {
        const max = parseFloat(this.getAttribute('max'));
        return isFinite(max) && max > min ? max : min + 1;
    }

    /**
     * Resolves the effective `value`, defaulting to `0` when missing or malformed and
     * clamping it to the `min`/`max` range.
     *
     * @param {number} min - The resolved minimum value.
     * @param {number} max - The resolved maximum value.
     * @returns {number} The resolved value.
     */
    resolveValue(min, max) {
        const value = parseFloat(this.getAttribute('value'));
        const resolvedValue = isFinite(value) ? value : 0;
        return Math.min(Math.max(resolvedValue, min), max);
    }

    /**
     * Resolves the effective `low` bound, defaulting to `min` when missing or malformed
     * and clamping it to the `min`/`max` range.
     *
     * @param {number} min - The resolved minimum value.
     * @param {number} max - The resolved maximum value.
     * @returns {number} The resolved low bound.
     */
    resolveLow(min, max) {
        const low = parseFloat(this.getAttribute('low'));
        const resolvedLow = isFinite(low) ? low : min;
        return Math.min(Math.max(resolvedLow, min), max);
    }

    /**
     * Resolves the effective `high` bound, defaulting to `max` when missing or malformed,
     * clamping it to the `min`/`max` range, and raising it to `low` if it would otherwise
     * fall below `low`.
     *
     * @param {number} min - The resolved minimum value.
     * @param {number} max - The resolved maximum value.
     * @param {number} low - The resolved low bound.
     * @returns {number} The resolved high bound.
     */
    resolveHigh(min, max, low) {
        const high = parseFloat(this.getAttribute('high'));
        const resolvedHigh = isFinite(high) ? high : max;
        const clampedHigh = Math.min(Math.max(resolvedHigh, min), max);
        return Math.max(clampedHigh, low);
    }

    /**
     * Resolves the effective `optimum` value, defaulting to the midpoint of `min`/`max`
     * when missing or malformed and clamping it to the `min`/`max` range.
     *
     * @param {number} min - The resolved minimum value.
     * @param {number} max - The resolved maximum value.
     * @returns {number} The resolved optimum value.
     */
    resolveOptimum(min, max) {
        const optimum = parseFloat(this.getAttribute('optimum'));
        const resolvedOptimum = isFinite(optimum) ? optimum : min + (max - min) / 2;
        return Math.min(Math.max(resolvedOptimum, min), max);
    }

    /**
     * Mirrors the native <meter> gauge-region algorithm (WHATWG/WebKit) to classify the
     * current value as `'low'`, `'medium'`, or `'high'` relative to `low`/`high`/`optimum`.
     *
     * @param {{value: number, min: number, max: number, low: number, high: number, optimum: number}} range - The resolved range values.
     * @returns {'low'|'medium'|'high'} The computed gauge region.
     */
    resolveGaugeRegion({ value, min, max, low, high, optimum }) {
        if (optimum < low) {
            if (value <= low) { return 'high'; }
            if (value <= high) { return 'medium'; }
            return 'low';
        }

        if (optimum > high) {
            if (value >= high) { return 'high'; }
            if (value >= low) { return 'medium'; }
            return 'low';
        }

        if (value >= low && value <= high) {
            return 'high';
        }

        const midpoint = min + (max - min) / 2;
        if (value < midpoint) {
            return value >= low ? 'medium' : 'low';
        }
        return value <= high ? 'medium' : 'low';
    }

    /**
     * Links the label and supporting text elements to the `<meter>` element via
     * `aria-labelledby` and `aria-describedby`, generating unique ids as needed.
     *
     * @returns {void}
     */
    handleAccessibilityAttributes() {
        const meterElement = this.shadowRoot.querySelector('meter');
        const labelElement = this.shadowRoot.querySelector('label');
        const supportingTextElement = this.shadowRoot.querySelector('span');

        if (!meterElement || !labelElement || !supportingTextElement) {
            return;
        }

        const idBase = Math.random().toString(36).substr(2, 9);
        labelElement.id = `neptune-meter-label-${idBase}`;
        supportingTextElement.id = `neptune-meter-support-${idBase}`;

        meterElement.setAttribute('aria-labelledby', labelElement.id);
        meterElement.setAttribute('aria-describedby', supportingTextElement.id);
    }

    /**
     * Sets the trimmed label text shown above the meter.
     *
     * @param {string|null} labelText - The label text to display.
     * @returns {void}
     */
    setLabel(labelText) {
        const labelElement = this.shadowRoot.querySelector('label');
        if (!labelElement) {
            return;
        }

        labelElement.textContent = (labelText || '').trim();
    }

    /**
     * Sets the trimmed supporting text shown below the meter.
     *
     * @param {string|null} supportingText - The supporting text to display.
     * @returns {void}
     */
    setSupportingText(supportingText) {
        const supportingTextElement = this.shadowRoot.querySelector('span');
        if (!supportingTextElement) {
            return;
        }

        supportingTextElement.textContent = (supportingText || '').trim();
    }

}

customElements.define('neptune-meter', Meter);
export default Meter;
